import clsx from "clsx";
import { Bed, Bath, Maximize, Users } from "lucide-react";
import moment from "moment";

// Helper function to format relative time using moment
function getRelativeTime(dateString: string): string {
    if (!dateString) {
        return "";
    }
    const parsedDate = moment(dateString, moment.ISO_8601, true);
    if (!parsedDate.isValid()) {
        return "";
    }
    return parsedDate.fromNow();
}

interface PropertyStats {
    avgFirstGuess: number;
    avgScore: number;
    highestGuess: number;
    lowestGuess: number;
    totalGuessers: number;
}

interface PropertyCardProps {
    image: string;
    address: string;
    city: string;
    state: string;
    guessersCount: number;
    datePlayable: string;
    beds?: number;
    baths?: number;
    sqft?: number;
    propertyStats?: PropertyStats;
    viewerHasPlayed?: boolean;
    onClick?: () => void;
    index: number;
    showScoreIndex: number | null;
    onToggleScore: (index: number) => void;
}

export function PropertyCard({
    image,
    address,
    city,
    state,
    guessersCount,
    datePlayable,
    beds,
    baths,
    sqft,
    propertyStats,
    viewerHasPlayed = false,
    onClick,
    index,
    showScoreIndex,
    onToggleScore,
}: PropertyCardProps) {
    const showScore = showScoreIndex === index;
    const shouldHideStats = !viewerHasPlayed;

    // Format price with $ and commas
    const formatPrice = (num: number) => {
        return "$" + num.toLocaleString();
    };

    const renderProtectedValue = ({
        value,
        className,
        ariaLabel = "Play this puzzle to reveal this stat",
    }: {
        value: string;
        className: string;
        ariaLabel?: string;
    }) => {
        if (!shouldHideStats) {
            return <span className={className}>{value}</span>;
        }
        return (
            <span className={className}>
                <span aria-hidden="true" className="tw-blur-sm tw-select-none">
                    {value}
                </span>
                <span className="tw-sr-only">{ariaLabel}</span>
            </span>
        );
    };

    return (
        <div
            className="tw-group tw-bg-black tw-rounded-2xl tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-shadow-gray-900/50 tw-cursor-pointer"
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onClick?.();
                }
            }}
        >
            {/* Property Image or Stats View */}
            <div className="tw-relative tw-w-full tw-h-[280px] sm:tw-h-[280px] md:tw-h-[280px] tw-overflow-hidden">
                {!showScore ? (
                    // Image View
                    <div className="tw-w-full tw-h-full">
                        <img
                            src={image}
                            alt={address}
                            className="tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-110"
                        />
                    </div>
                ) : (
                    // Property Stats View - styled like Community Stats from Scoreboard
                    <div className="tw-w-full tw-h-full tw-bg-[#1A1A1A] tw-flex tw-flex-col tw-justify-between tw-p-3">
                        <h4 className="tw-text-white tw-font-semibold tw-text-sm tw-mb-3">Community Stats</h4>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-flex-1 tw-mb-3">
                            {/* Average First Guess + Average Score */}
                            <div className="tw-bg-[#2E2E2E] tw-rounded-md tw-p-2 tw-flex tw-flex-col tw-gap-1">
                                <span className="tw-text-xs tw-text-gray-400">Average First Guess</span>
                                {renderProtectedValue({
                                    value: formatPrice(propertyStats?.avgFirstGuess ?? 0),
                                    className: "tw-font-semibold tw-text-base tw-text-[#F69929]",
                                })}
                                <span className="tw-text-xs tw-text-gray-400 tw-mt-1">Average Score</span>
                                {renderProtectedValue({
                                    value: propertyStats?.avgScore != null ? propertyStats?.avgScore.toFixed(1) : "N/A",
                                    className: "tw-font-semibold tw-text-base tw-text-[#BB84FF]",
                                })}
                            </div>
                            {/* Right column: Highest + Lowest */}
                            <div className="tw-flex tw-flex-col tw-gap-2">
                                <div className="tw-bg-[#2E2E2E] tw-rounded-md tw-p-2 tw-flex tw-flex-col tw-gap-1 tw-flex-1">
                                    <span className="tw-text-xs tw-text-gray-400">Highest Guess</span>
                                    {renderProtectedValue({
                                        value: formatPrice(propertyStats?.highestGuess ?? 0),
                                        className: "tw-font-semibold tw-text-base tw-text-white",
                                    })}
                                </div>
                                <div className="tw-bg-[#2E2E2E] tw-rounded-md tw-p-2 tw-flex tw-flex-col tw-gap-1 tw-flex-1">
                                    <span className="tw-text-xs tw-text-gray-400">Lowest Guess</span>
                                    {renderProtectedValue({
                                        value: formatPrice(propertyStats?.lowestGuess ?? 0),
                                        className: "tw-font-semibold tw-text-base tw-text-white",
                                    })}
                                </div>
                            </div>
                        </div>
                        {/* Total Guessers */}
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                            <Users className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
                            <span className="tw-text-xs tw-text-gray-400">
                                {renderProtectedValue({
                                    value: (propertyStats?.totalGuessers ?? 0).toLocaleString(),
                                    className: "tw-text-white tw-font-semibold",
                                    ariaLabel: "Play this puzzle to reveal how many users guessed",
                                })}{" "}
                                users guessed
                            </span>
                        </div>

                        {/* Property Info - Part of stats view */}
                        <div className="tw-bg-[#1A1A1A] tw-p-2 tw--mx-3 tw--mb-3">
                            <h3 className="tw-font-['Merriweather',serif] tw-font-normal tw-text-xs tw-mb-0.5 tw-truncate tw-text-white">
                                {address}
                            </h3>
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-1 tw-text-[10px] tw-text-white/70">
                                {(city || state) && (
                                    <p className="tw-font-light tw-truncate tw-text-white/70 tw-flex-1 tw-min-w-0">
                                        {[city, state].filter(Boolean).join(", ")}
                                    </p>
                                )}
                                {(beds || baths || sqft) && (
                                    <div className="tw-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0 tw-text-white/70">
                                        {beds && (
                                            <div className="tw-flex tw-items-center tw-gap-0.5">
                                                <Bed className="tw-w-2.5 tw-h-2.5" />
                                                <span className="tw-font-light">{beds}</span>
                                            </div>
                                        )}
                                        {baths && (
                                            <div className="tw-flex tw-items-center tw-gap-0.5">
                                                <Bath className="tw-w-2.5 tw-h-2.5" />
                                                <span className="tw-font-light">{baths}</span>
                                            </div>
                                        )}
                                        {sqft && (
                                            <div className="tw-flex tw-items-center tw-gap-0.5">
                                                <Maximize className="tw-w-2.5 tw-h-2.5" />
                                                <span className="tw-font-light">{sqft.toLocaleString()}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="tw-text-[9px] tw-font-light tw-mt-0.5 tw-text-white/50">
                                {getRelativeTime(datePlayable) && (
                                    <div className="tw-text-[9px] tw-font-light tw-mt-0.5 tw-text-white/50">
                                        Shared {getRelativeTime(datePlayable)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Property Info Overlay - Only show when image is displayed */}
                {!showScore && (
                    <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-2 sm:tw-p-3 md:tw-p-4 tw-bg-gradient-to-t tw-from-black/90 tw-via-black/60 tw-to-transparent">
                        <h3 className="tw-font-['Merriweather',serif] tw-font-normal tw-text-xs sm:tw-text-sm tw-mb-0.5 tw-truncate md:tw-text-base tw-text-white">
                            {address}
                        </h3>
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-[10px] sm:tw-text-xs tw-text-white/70 tw-w-full tw-min-w-0">
                            {(beds || baths || sqft) && (
                                <div className="tw-flex tw-items-center tw-gap-0.5 sm:tw-gap-1 tw-flex-shrink-0 tw-text-white/70">
                                    {beds && (
                                        <div className="tw-flex tw-items-center tw-gap-0.5">
                                            <Bed className="tw-w-2.5 tw-h-2.5 sm:tw-w-3 sm:tw-h-3" />
                                            <span className="tw-font-light">{beds}</span>
                                        </div>
                                    )}
                                    {baths && (
                                        <div className="tw-flex tw-items-center tw-gap-0.5">
                                            <Bath className="tw-w-2.5 tw-h-2.5 sm:tw-w-3 sm:tw-h-3" />
                                            <span className="tw-font-light">{baths}</span>
                                        </div>
                                    )}
                                    {sqft && (
                                        <div className="tw-flex tw-items-center tw-gap-0.5">
                                            <Maximize className="tw-w-2.5 tw-h-2.5 sm:tw-w-3 sm:tw-h-3" />
                                            <span className="tw-font-light">{sqft.toLocaleString()}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="tw-text-[9px] sm:tw-text-[10px] md:tw-text-xs tw-font-light tw-mt-0.5 tw-text-white/50">
                            Shared {getRelativeTime(datePlayable)}
                        </div>
                    </div>
                )}

                {/* Toggle Icon - Only show if there are guessers */}
                {guessersCount > 0 && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleScore(index);
                        }}
                        className="tw-absolute tw-top-2.5 tw-right-2.5 md:tw-top-3 md:tw-right-3 tw-bg-white/95 tw-backdrop-blur-md tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-white tw-transition-all tw-shadow-[0_4px_12px_rgba(124,58,237,0.3)] hover:tw-shadow-[0_4px_16px_rgba(124,58,237,0.4)] hover:tw-scale-105 md:tw-px-3.5 md:tw-py-2.5 tw-group/toggle"
                        aria-label={showScore ? "Show image" : "Show stats"}
                    >
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                            {!showScore ? (
                                <>
                                    {/* Guessers count badge - responsive sizing */}
                                    <div
                                        className={clsx(
                                            "tw-absolute tw--top-1.5 tw--right-1.5",
                                            "tw-bg-gradient-to-br tw-from-[#7c3aed] tw-to-[#6d28d9]",
                                            "tw-text-white tw-font-bold tw-rounded-full",
                                            "tw-flex tw-items-center tw-justify-center tw-shadow-md",
                                            "tw-min-w-5 tw-h-5",
                                            {
                                                "tw-w-5 tw-text-xs": guessersCount < 10,
                                                "tw-w-6 tw-text-[10px]": guessersCount >= 10 && guessersCount < 100,
                                                "tw-px-1 tw-text-[9px]": guessersCount >= 100,
                                            },
                                        )}
                                    >
                                        {guessersCount > 999 ? "999+" : guessersCount}
                                    </div>

                                    {/* Show Stats Icon - Grid of colored squares (3x3) using arrays */}
                                    <div className={clsx("tw-flex tw-gap-0.5 sm:tw-gap-1")}>
                                        {[
                                            ["#7c3aed", "#f69929", "gray-300"],
                                            ["#7c3aed", "gray-300", "#f69929"],
                                            ["#f69929", "#7c3aed", "gray-300"],
                                        ].map((column, colIndex) => (
                                            <div
                                                key={colIndex}
                                                className={clsx("tw-flex tw-flex-col tw-gap-0.5 sm:tw-gap-1")}
                                            >
                                                {column.map((color, rowIndex) => (
                                                    <div
                                                        key={rowIndex}
                                                        className={clsx(
                                                            "tw-rounded-sm",
                                                            "tw-w-1.5 tw-h-1.5 sm:tw-w-2 sm:tw-h-2",
                                                            {
                                                                [`tw-bg-${color}`]:
                                                                    color === "gray-300" && "tw-bg-gray-300",
                                                            },
                                                        )}
                                                        style={color.startsWith("#") ? { backgroundColor: color } : {}}
                                                    />
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Show Image Icon - matched to grid icon size */}
                                    <div className="tw-w-[18px] tw-h-[18px] tw-border-[1.5px] tw-border-gray-600 tw-rounded-sm tw-relative">
                                        <div className="tw-absolute tw-top-0.5 tw-right-0.5 tw-w-1 tw-h-1 tw-bg-[#f69929] tw-rounded-full" />
                                        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-h-[7px] tw-bg-gray-600 tw-rounded-b-sm" />
                                    </div>
                                </>
                            )}
                        </div>
                    </button>
                )}
            </div>
        </div>
    );
}
